import { useState } from "react";
import { Plus, Minus, Phone, ArrowUpRight, HelpCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type FAQ = {
  q: string;
  a: string;
};
const FAQS: FAQ[] = [{
  q: "How much does it cost to repair a roller door in Sydney?",
  a: "Most common roller door repairs sit between $180 and $650 depending on the fault. A simple cable replacement or motor service starts around $180. Spring + cable on a double door runs $380–$520. Full motor replacement is $480–$850 depending on brand. We always send a fixed-price quote on-site before any work starts — no surprises mid-job, and the callout is free if you don't proceed."
}, {
  q: "How long does a typical roller door repair take?",
  a: "Most repairs are completed on the first visit in 45–90 minutes. Spring and cable jobs typically take 60 minutes. Motor swaps run 90 minutes. Curtain or slat replacements can take 2–3 hours depending on door size. Since we carry common parts in the van, we rarely need a second visit."
}, {
  q: "Do you service my roller door brand?",
  a: "Yes — every major brand sold in Australia. B&D Doors, Gliderol, Steel-Line, Merlin, Chamberlain, ATA, Centurion, Boss, Superlift, Dominator, Eco-Plus and more. If you're not sure what brand you have, send us a photo of the door or motor unit on WhatsApp (0485 000 933) and we'll identify it within minutes."
}, {
  q: "Is the callout fee free?",
  a: "Yes. The callout and on-site diagnosis are completely free — you only pay if you approve our fixed-price quote. There's no obligation to proceed, no deposit, and no minimum service charge. If our quote is higher than expected, you keep the diagnosis and we walk away."
}, {
  q: "Can you come the same day?",
  a: "In most cases, yes. We cover all of Sydney including Eastern Suburbs, Inner West, Northern Beaches, North Shore, Hills District, Western Sydney, Sutherland Shire, plus Wollongong and the Central Coast. Average response time is 22 minutes from the first call to confirmed arrival window. For after-hours emergencies, see our 24/7 emergency service."
}, {
  q: "What kind of warranty do you offer?",
  a: "Every job includes a 12-month workmanship warranty in writing, plus the manufacturer's warranty on any new parts (springs, cables, motors typically come with 12–24 months). If anything we installed fails inside the warranty period, we come back and fix it at no cost. Warranty details are listed on your written invoice."
}, {
  q: "My roller door is making a loud grinding noise — is it dangerous?",
  a: "It depends. Grinding usually means worn bearings, dry tracks, or a damaged drum — none of which are immediately dangerous but will get worse fast and can lead to a full failure. Snapping or popping sounds (especially loud bangs) often signal a broken spring or cable, which IS dangerous — stop using the door and call us. We can usually diagnose by phone or a short video."
}, {
  q: "Can you install a new roller door or only repair?",
  a: "Both. We supply and install new roller doors from all major brands, plus replacements for damaged or end-of-life doors. Free measure and quote on-site. New installs typically include removal and disposal of the old door, motor pairing, remote programming, and a full safety check. Lead time is usually 5–10 business days depending on the brand and colour."
}];
export default function RollerFAQ() {
  const [open, setOpen] = useState<number | null>(0);

  // JSON-LD for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(faq => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a
      }
    }))
  };
  return <section style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      {/* JSON-LD Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(jsonLd)
    }} />

      {/* Atmospheric gradients */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      background: `radial-gradient(circle at 10% 20%, rgba(245,166,35,0.06) 0%, transparent 50%), radial-gradient(circle at 90% 80%, rgba(245,166,35,0.04) 0%, transparent 50%)`,
      pointerEvents: "none"
    }} />

      {/* Grain */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      opacity: 0.03,
      mixBlendMode: "overlay",
      pointerEvents: "none",
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Watermark */}
      <div aria-hidden style={{
      position: "absolute",
      top: 60,
      right: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 280px)",
      fontWeight: 900,
      color: "rgba(255,255,255,0.02)",
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 1
    }}>
        FAQ
      </div>

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 64,
        alignItems: "end"
      }} className="roller-faq-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                08 / Common questions
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              The questions Sydney homeowners{" "}
              <span style={{
              color: GOLD,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                ask us most.
              </span>
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: "rgba(255,255,255,0.7)",
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            Don't see your question? Call or WhatsApp us — we answer roller-door
            questions every day, no obligation.
          </p>
        </div>

        {/* === MAIN BODY: ACCORDION + STICKY CTA === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.7fr) minmax(0, 1fr)",
        gap: 48,
        alignItems: "start"
      }} className="roller-faq-body">
          {/* LEFT — Accordion */}
          <div style={{
          display: "flex",
          flexDirection: "column"
        }}>
            {FAQS.map((faq, i) => <FaqRow key={i} faq={faq} index={i} isOpen={open === i} isLast={i === FAQS.length - 1} onToggle={() => setOpen(open === i ? null : i)} />)}
          </div>

          {/* RIGHT — Sticky CTA card */}
          <div className="roller-faq-sticky" style={{
          position: "sticky",
          top: 100,
          padding: "40px 32px",
          background: NAVY_DEEP,
          border: `1px solid rgba(245,166,35,0.2)`,
          borderRadius: 4,
          overflow: "hidden"
        }}>
            {/* Gold corner */}
            <div aria-hidden style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 3,
            height: "100%",
            background: GOLD
          }} />

            {/* Soft glow */}
            <div aria-hidden style={{
            position: "absolute",
            top: "-30%",
            right: "-30%",
            width: 300,
            height: 300,
            background: `radial-gradient(circle, rgba(245,166,35,0.1) 0%, transparent 60%)`,
            pointerEvents: "none"
          }} />

            <div style={{
            position: "relative"
          }}>
              <div style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: "rgba(245,166,35,0.15)",
              border: `1px solid rgba(245,166,35,0.3)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20
            }}>
                <HelpCircle size={24} color={GOLD} strokeWidth={1.8} />
              </div>

              <div style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 12
            }}>
                Still got questions?
              </div>

              <h3 style={{
              fontFamily: FONT_HEAD,
              fontSize: 22,
              fontWeight: 700,
              color: "#ffffff",
              lineHeight: 1.25,
              letterSpacing: "-0.015em",
              margin: "0 0 16px"
            }}>
                Speak to a roller-door technician now.
              </h3>

              <p style={{
              fontFamily: FONT_BODY,
              fontSize: 14,
              color: "rgba(255,255,255,0.65)",
              lineHeight: 1.55,
              margin: "0 0 28px"
            }}>
                Free advice over the phone — no high-pressure sales, no
                callout fee. We'll give you a real answer in plain English.
              </p>

              <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 10
            }}>
                <a href="tel:0485000933" style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: "16px 24px",
                background: GOLD,
                color: NAVY,
                fontFamily: FONT_HEAD,
                fontSize: 15,
                fontWeight: 700,
                borderRadius: 8,
                textDecoration: "none",
                transition: "transform 0.2s, box-shadow 0.2s",
                boxShadow: "0 6px 20px rgba(245,166,35,0.3)"
              }} onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.45)";
              }} onMouseLeave={e => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.3)";
              }}>
                  <Phone size={16} strokeWidth={2.5} />
                  0485 000 933
                </a>
                <Link to="#contact-us" style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "14px 24px",
                background: "transparent",
                color: "#ffffff",
                fontFamily: FONT_HEAD,
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 8,
                textDecoration: "none",
                border: "1px solid rgba(255,255,255,0.25)",
                transition: "background 0.2s, border-color 0.2s"
              }} onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.08)";
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.5)";
              }} onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.25)";
              }}>
                  Free quote form
                  <ArrowUpRight size={14} strokeWidth={2.5} />
                </Link>
              </div>

              {/* Operating hours */}
              <div style={{
              marginTop: 28,
              paddingTop: 24,
              borderTop: "1px solid rgba(255,255,255,0.08)"
            }}>
                <div style={{
                fontFamily: FONT_BODY,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: GOLD_DARK,
                marginBottom: 8
              }}>
                  Operating hours
                </div>
                <div style={{
                fontFamily: FONT_BODY,
                fontSize: 13,
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.6
              }}>
                  
                  
                  <div style={{
                  color: GOLD,
                  fontWeight: 600,
                  marginTop: 6
                }}>24/7 emergency</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .roller-faq-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .roller-faq-body {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .roller-faq-sticky {
            position: static !important;
          }
        }
      `}</style>
    </section>;
}

// === Accordion row ===
function FaqRow({
  faq,
  index,
  isOpen,
  isLast,
  onToggle
}: {
  faq: FAQ;
  index: number;
  isOpen: boolean;
  isLast: boolean;
  onToggle: () => void;
}) {
  return <div style={{
    borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.08)"
  }}>
      <button onClick={onToggle} aria-expanded={isOpen} style={{
      width: "100%",
      padding: "28px 0",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      textAlign: "left",
      display: "grid",
      gridTemplateColumns: "auto 1fr auto",
      gap: 20,
      alignItems: "center",
      color: "#ffffff",
      fontFamily: FONT_HEAD,
      transition: "color 0.2s"
    }} onMouseEnter={e => {
      (e.currentTarget.children[1] as HTMLElement).style.color = GOLD;
    }} onMouseLeave={e => {
      (e.currentTarget.children[1] as HTMLElement).style.color = isOpen ? GOLD : "#ffffff";
    }}>
        <span style={{
        fontFamily: FONT_HEAD,
        fontSize: 13,
        fontWeight: 800,
        color: isOpen ? GOLD : "rgba(255,255,255,0.35)",
        letterSpacing: "0.08em",
        minWidth: 28,
        transition: "color 0.2s"
      }}>
          {String(index + 1).padStart(2, "0")}
        </span>

        <span style={{
        fontFamily: FONT_HEAD,
        fontSize: "clamp(16px, 1.4vw, 19px)",
        fontWeight: 600,
        color: isOpen ? GOLD : "#ffffff",
        lineHeight: 1.35,
        letterSpacing: "-0.005em",
        transition: "color 0.2s"
      }}>
          {faq.q}
        </span>

        <span style={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: isOpen ? GOLD : "rgba(255,255,255,0.05)",
        border: isOpen ? `1px solid ${GOLD}` : "1px solid rgba(255,255,255,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background 0.2s, border-color 0.2s"
      }}>
          {isOpen ? <Minus size={16} color={NAVY} strokeWidth={2.5} /> : <Plus size={16} color="#ffffff" strokeWidth={2.5} />}
        </span>
      </button>

      {/* Answer */}
      <div style={{
      maxHeight: isOpen ? 500 : 0,
      overflow: "hidden",
      transition: "max-height 0.4s ease, padding 0.3s ease",
      paddingBottom: isOpen ? 28 : 0
    }}>
        <div style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 20
      }}>
          <div style={{
          minWidth: 28
        }} />
          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 15,
          color: "rgba(255,255,255,0.75)",
          lineHeight: 1.7,
          margin: 0,
          maxWidth: 680
        }}>
            {faq.a}
          </p>
          <div style={{
          width: 36
        }} />
        </div>
      </div>
    </div>;
}